import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

export interface PartnerProduct {
  id: string;
  partner_id: string;
  name: string;
  price_per_m2: number;
  active: boolean;
  created_at: string;
  description: string;
  image_url: string | null;
  specs: string[];
  usps: string[];
  sort_order: number;
}

export interface PartnerExtra {
  id: string;
  partner_id: string;
  name: string;
  price: number;
  unit: 'm2' | 'vast';
  active: boolean;
}

const MOCK_PRODUCTS: PartnerProduct[] = [
  { id: 'pp-1', partner_id: 'partner-003', name: 'Houtlook Premium PVC',         price_per_m2: 84.95,  active: true, created_at: '2024-01-01T00:00:00Z', description: '', image_url: null, specs: [], usps: [], sort_order: 0 },
  { id: 'pp-2', partner_id: 'partner-003', name: 'Betonlook Premium PVC',         price_per_m2: 89.95,  active: true, created_at: '2024-01-01T00:00:00Z', description: '', image_url: null, specs: [], usps: [], sort_order: 1 },
  { id: 'pp-3', partner_id: 'partner-003', name: 'Tegellook Premium PVC',         price_per_m2: 89.95,  active: true, created_at: '2024-01-01T00:00:00Z', description: '', image_url: null, specs: [], usps: [], sort_order: 2 },
  { id: 'pp-4', partner_id: 'partner-003', name: 'Visgraat Premium PVC',          price_per_m2: 109.95, active: true, created_at: '2024-01-01T00:00:00Z', description: '', image_url: null, specs: [], usps: [], sort_order: 3 },
  { id: 'pp-5', partner_id: 'partner-003', name: 'Hongaarse Punt Premium PVC',    price_per_m2: 124.95, active: true, created_at: '2024-01-01T00:00:00Z', description: '', image_url: null, specs: [], usps: [], sort_order: 4 },
];

const MOCK_EXTRAS: PartnerExtra[] = [
  { id: 'pe-1', partner_id: 'partner-003', name: 'Tussenvloer / egaliseren', price: 7.40,   unit: 'm2',   active: true },
  { id: 'pe-2', partner_id: 'partner-003', name: 'Lift (toeslag)',            price: 110.00, unit: 'vast', active: true },
  { id: 'pe-3', partner_id: 'partner-003', name: 'Afvoeren + storten',        price: 50.00,  unit: 'vast', active: true },
  { id: 'pe-4', partner_id: 'partner-003', name: 'Leveringskosten',           price: 137.00, unit: 'vast', active: true },
];

function mapProduct(row: any): PartnerProduct {
  return {
    id: row.id,
    partner_id: row.partner_id,
    name: row.name,
    price_per_m2: Number(row.price_per_m2) || 0,
    active: row.active ?? true,
    created_at: row.created_at,
    description: row.description || '',
    image_url: row.image_url || null,
    specs: Array.isArray(row.specs) ? row.specs : [],
    usps: Array.isArray(row.usps) ? row.usps : [],
    sort_order: row.sort_order || 0,
  };
}

function mapExtra(row: any): PartnerExtra {
  return {
    id: row.id,
    partner_id: row.partner_id,
    name: row.name,
    price: Number(row.price) || 0,
    unit: (row.unit === 'm2' ? 'm2' : 'vast') as 'm2' | 'vast',
    active: row.active ?? true,
  };
}

export function usePartnerCatalogue(partnerId: string | null) {
  const [products, setProducts] = useState<PartnerProduct[]>([]);
  const [extras, setExtras] = useState<PartnerExtra[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!partnerId) { setLoading(false); return; }

    if (!isSupabaseConfigured) {
      setProducts(MOCK_PRODUCTS.filter(p => p.partner_id === partnerId));
      setExtras(MOCK_EXTRAS.filter(e => e.partner_id === partnerId));
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const [{ data: prods }, { data: exts }] = await Promise.all([
          supabase.from('partner_products').select('*').eq('partner_id', partnerId).order('sort_order').order('created_at'),
          supabase.from('partner_extras').select('*').eq('partner_id', partnerId).order('name'),
        ]);
        setProducts((prods || []).map(mapProduct));
        setExtras((exts || []).map(mapExtra));
      } catch {
        setProducts(MOCK_PRODUCTS.filter(p => p.partner_id === partnerId));
        setExtras(MOCK_EXTRAS.filter(e => e.partner_id === partnerId));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [partnerId]);

  const saveProduct = useCallback(async (product: PartnerProduct) => {
    setProducts(prev => prev.map(p => p.id === product.id ? product : p));
    if (!isSupabaseConfigured) return;
    await supabase
      .from('partner_products')
      .update({
        name:          product.name,
        price_per_m2:  product.price_per_m2,
        active:        product.active,
        description:   product.description,
        image_url:     product.image_url,
        specs:         product.specs,
        usps:          product.usps,
        sort_order:    product.sort_order,
      })
      .eq('id', product.id);
  }, []);

  const saveExtra = useCallback(async (extra: PartnerExtra) => {
    setExtras(prev => prev.map(e => e.id === extra.id ? extra : e));
    if (!isSupabaseConfigured) return;
    await supabase
      .from('partner_extras')
      .update({ name: extra.name, price: extra.price, unit: extra.unit, active: extra.active })
      .eq('id', extra.id);
  }, []);

  const addProduct = useCallback(async (pid: string): Promise<string | null> => {
    const payload = { partner_id: pid, name: 'Nieuw product', price_per_m2: 0, active: true, description: '', specs: [], usps: [], sort_order: 0 };
    if (!isSupabaseConfigured) {
      const row: PartnerProduct = { ...payload, id: `mock-p-${Date.now()}`, created_at: new Date().toISOString(), image_url: null };
      setProducts(prev => [...prev, row]);
      return row.id;
    }
    const { data } = await supabase.from('partner_products').insert(payload).select().single();
    if (data) setProducts(prev => [...prev, mapProduct(data)]);
    return data?.id || null;
  }, []);

  const addExtra = useCallback(async (pid: string): Promise<string | null> => {
    const payload = { partner_id: pid, name: 'Nieuwe extra', price: 0, unit: 'vast', active: true };
    if (!isSupabaseConfigured) {
      const row: PartnerExtra = { ...payload, id: `mock-e-${Date.now()}`, unit: 'vast' };
      setExtras(prev => [...prev, row]);
      return row.id;
    }
    const { data } = await supabase.from('partner_extras').insert(payload).select().single();
    if (data) setExtras(prev => [...prev, mapExtra(data)]);
    return data?.id || null;
  }, []);

  const deleteProduct = useCallback(async (id: string) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    if (!isSupabaseConfigured) return;
    await supabase.from('partner_products').delete().eq('id', id);
  }, []);

  const deleteExtra = useCallback(async (id: string) => {
    setExtras(prev => prev.filter(e => e.id !== id));
    if (!isSupabaseConfigured) return;
    await supabase.from('partner_extras').delete().eq('id', id);
  }, []);

  const uploadProductImage = useCallback(async (
    productId: string,
    partnerId: string,
    file: File,
  ): Promise<string | null> => {
    if (!isSupabaseConfigured) return null;
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${partnerId}/products/${productId}.${ext}`;
    const { error } = await supabase.storage
      .from('semmy-avatars')
      .upload(path, file, { upsert: true });
    if (error) {
      console.error('uploadProductImage error:', error.message);
      return null;
    }
    const { data } = supabase.storage.from('semmy-avatars').getPublicUrl(path);
    return data.publicUrl;
  }, []);

  return {
    products, extras, loading,
    saveProduct, saveExtra,
    addProduct, addExtra,
    deleteProduct, deleteExtra,
    uploadProductImage,
  };
}
